/**
 * FILE: apps/hub/src/lib/queryKeys.ts
 * PURPOSE: Centralized React Query key registry. All QK.* definitions live here.
 * AGENT_CONTEXT: CRITICAL — Add new query keys here. Never hardcode strings in pages.
 * INVARIANTS: Every new data fetch must have a QK entry. Use arrays for parametrized keys.
 * LAST_REVIEWED: 2026-05-07
 */

/** Centralized React Query key registry. Use QK.* everywhere — never hardcode strings. */
export const QK = {
  dashboard: () => ['dashboard'] as const,
  students: (filters: object, page: number, sortField: string, sortDir: string) =>
    ['students', filters, page, sortField, sortDir] as const,
  studentsBase: () => ['students'] as const,
  studentCityOptions: (filterContext: object) => ['student-city-options', filterContext] as const,
  student: (phone: string) => ['student', phone] as const,
  studentActivity: (phone: string) => ['student-activity', phone] as const,
  studentEnrollments: (phone: string) => ['student-enrollments', phone] as const,
  studentExtensions: (phone: string) => ['student-extensions', phone] as const,
  studentNotes: (phone: string) => ['student-notes', phone] as const,
  phoneChangeHistory: (phone: string) => ['phone-change-history', phone] as const,
  enrollments: (phone: string) => ['enrollments', phone] as const,
  extensions: (phone: string) => ['extensions', phone] as const,
  activityLog: (filters: object, page: number) => ['activity-log', filters, page] as const,
  jobs: (filters: object, page: number) => ['jobs', filters, page] as const,
  job: (id: string) => ['job', id] as const,
  candidates: (filters: object, page: number) => ['candidates', filters, page] as const,
  candidatesBase: () => ['candidates'] as const,
  candidate: (phone: string) => ['candidate', phone] as const,
  applications: (filters: object) => ['applications', filters] as const,
  applicationsForJob: (jobId: string) => ['applications-for-job', jobId] as const,
  companies: () => ['companies'] as const,
  company: (id: string) => ['company', id] as const,
  recruiters: () => ['recruiters'] as const,
  recruitersByCompany: (companyId: string) => ['recruiters-by-company', companyId] as const,
  analytics: (staffId: string, role: string) => ['analytics', staffId, role] as const,
  analyticsTrend: () => ['analytics-trend'] as const,
  analyticsTrendV2: () => ['analytics-trend-v2'] as const,
  analyticsRevenuePulse: () => ['analytics-revenue-pulse'] as const,
  analyticsCourseBreakdown: () => ['analytics-course-breakdown'] as const,
  analyticsAudienceInsights: () => ['analytics-audience-insights'] as const,
  portalAnalytics: (periodDays: number) => ['portal-analytics', periodDays] as const,
  notifications: (staffId: string, role: string) => ['notifications', staffId, role] as const,
  staffMembers: () => ['staff-members'] as const,
  customRoles: () => ['custom-roles'] as const,
  shortlists: (jobId: string) => ['shortlists', jobId] as const,

  extendStudent: (phone: string) => ['extend-student', phone] as const,
  editStudent: (phone: string) => ['edit-student', phone] as const,
  studentByPhone: (phone: string) => ['student-by-phone', phone] as const,
  studentSearch: (q: string) => ['student-search', q] as const,
  installments: (phone: string) => ['installments', phone] as const,

  placementsRecruiters: () => ['placements-recruiters'] as const,
  placementsJobs: (statusFilter: string, recruiterFilter: string, companyFilter: string) =>
    ['placements-jobs', statusFilter, recruiterFilter, companyFilter] as const,
  placementsJobsBase: () => ['placements-jobs'] as const,
  placementsJobCounts: (idsKey: string) => ['placements-job-counts', idsKey] as const,
  placementsJobCountsBase: () => ['placements-job-counts'] as const,
  placementsJobDashboardCounts: () => ['placements-job-dashboard-counts'] as const,
  placementsTracker: () => ['placements-tracker'] as const,
  jobFormPocs: (companyId: string) => ['job-form-pocs', companyId] as const,
  placementsJob: (jobId: string) => ['placements-job', jobId] as const,
  placementsJobApps: (jobId: string) => ['placements-job-apps', jobId] as const,
  placementsApplicationLog: (appId: string | null) => ['placements-application-log', appId ?? ''] as const,
  placementsCompanies: () => ['placements-companies'] as const,
  placementsCompany: (id: string) => ['placements-company', id] as const,
  placementsCandidates: (search: string, expBandsKey: string, placementStatusesKey: string, courseKey?: string) =>
    ['placements-candidates', search, expBandsKey, placementStatusesKey, courseKey ?? ''] as const,
  placementsCandidatesBase: () => ['placements-candidates'] as const,
  placementsCandidatesRPC: (search: string, expBandsKey: string, placementStatusesKey: string) =>
    ['placements-candidates', 'rpc', search, expBandsKey, placementStatusesKey] as const,
  placementsCandidateDetail: (phone: string) => ['placements-candidate-detail', phone] as const,
  placementsCandidateApps: (phone: string) => ['placements-candidate-apps', phone] as const,
  placementsApplications: (statusesKey: string, jobId: string, dateFrom: string, dateTo: string) =>
    ['placements-applications', statusesKey, jobId, dateFrom, dateTo] as const,
  placementsApplicationsBase: () => ['placements-applications'] as const,
  applicationsDashboardRPC: (statusesKey: string, jobId: string, dateFrom: string, dateTo: string) =>
    ['placements-applications', 'rpc', statusesKey, jobId, dateFrom, dateTo] as const,
  placementsJobsDropdown: () => ['placements-jobs-dropdown'] as const,
  companyComboboxSearch: (debounced: string) => ['company-combobox-search', debounced] as const,
  companyComboboxSearchBase: () => ['company-combobox-search'] as const,

  /** Used by Dashboard.tsx → getDashboardStatsV2 (legacy ops route name retained). */
  opsDashboard: () => ['ops-dashboard'] as const,
  // Archived /ops pages removed from router — keys kept commented for reference only.
  // opsAlerts: () => ['ops-alerts'] as const,
  // opsBulkOps: () => ['ops-bulk-ops'] as const,
  // opsDataQuality: () => ['ops-data-quality'] as const,
  // opsHealth: (metric: string) => ['ops-health', metric] as const,
  // opsHealthLatest: () => ['ops-health-latest'] as const,
  // opsPushNotifications: () => ['ops-push-notifications'] as const,
  hubThreads: (status?: string) => ['hub-threads', status ?? 'all'] as const,
  hubThreadMessages: (threadId: string) => ['hub-thread-messages', threadId] as const,
  hubMentions: (staffId: string) => ['hub-mentions', staffId] as const,
  teamTasks: () => ['team-tasks'] as const,
  myTasks: (staffId: string) => ['my-tasks', staffId] as const,

  supportConversations: (filterKey: string, search: string) =>
    ['support-conversations', filterKey, search] as const,
  supportConversationsBase: () => ['support-conversations'] as const,
  supportMessages: (conversationId: string, page: number) =>
    ['support-messages', conversationId, page] as const,
  supportMessagesBase: () => ['support-messages'] as const,
  supportQuickReplies: (category: string) => ['support-quick-replies', category] as const,
  supportQuickRepliesBase: () => ['support-quick-replies'] as const,

  communityMonitorRooms: () => ['community-monitor', 'rooms'] as const,
  communityMonitorMessages: (roomId: string) => ['community-monitor', 'messages', roomId] as const,
  communityLinks: (type: string) => ['community-links', type] as const,
  missingCommunityLinks: () => ['missing-community-links'] as const,

  outreachFollowups: (filters: object, page: number) =>
    ['outreach-followups', filters, page] as const,
  outreachStats: () => ['outreach-stats'] as const,

  onboardingFollowups: (filters: object, page: number) =>
    ['onboarding-followups', filters, page] as const,
  onboardingStats: () => ['onboarding-stats'] as const,

  assistantHistory: (sessionId: string) => ['assistant-history', sessionId] as const,
  assistantSessions: () => ['assistant-sessions'] as const,

  placementsInbox: (tab: string, offset = 0) => ['placements-inbox', tab, offset] as const,
  placementsInboxBase: () => ['placements-inbox'] as const,
  placementsInboxRPC: (tab: string, search: string) =>
    ['placements-inbox', 'rpc', tab, search] as const,
  placementsChatConversations: () => ['placements-chat-conversations'] as const,
  placementsHistory: () => ['placements-history'] as const,

  notificationsPage: (staffId: string, roleName: string, mainTab: string, typeFilter: string) =>
    ['notifications-page', staffId, roleName, mainTab, typeFilter] as const,
  notificationsPageBase: (staffId: string, roleName: string) =>
    ['notifications-page', staffId, roleName] as const,
  mentionsPage: (staffId: string) => ['mentions-page', staffId] as const,
  mentionsPageBase: (staffId: string) => ['mentions-page', staffId] as const,
  notificationsDropdown: (staffId: string, roleName: string) =>
    ['notifications-dropdown', staffId, roleName] as const,
  notificationsDropdownBase: (staffId: string, roleName: string) =>
    ['notifications-dropdown', staffId, roleName] as const,

  studentDoubts: (status: string, assignedTo: string) => ['student-doubts', status, assignedTo] as const,
  studentDoubtsBase: () => ['student-doubts'] as const,

  courseSyllabus: (course: string) => ['course-syllabus', course] as const,
  liveClassSchedules: (course: string, attempt: string) => ['live-class-schedules', course, attempt] as const,
  liveClassSchedulesBase: () => ['live-class-schedules'] as const,

  websiteSyncSettings: () => ['website-sync-settings'] as const,
  websiteSyncHealth: () => ['website-sync-health'] as const,
  websiteSyncDashboard: () => ['website-sync-dashboard'] as const,
  websiteSyncCronErrors: () => ['website-sync-cron-errors'] as const,
  websiteCatalogHealth: () => ['website-catalog-health'] as const,
  websiteSyncReconciliation: () => ['website-sync-reconciliation'] as const,
  websiteSyncDailyReport: () => ['website-sync-daily-report'] as const,
  websiteSyncInternationalSummary: () => ['website-sync-international-summary'] as const,
  websiteSyncCatalogList: (search: string, page: number) =>
    ['website-sync-catalog-list', search, page] as const,
  websiteSyncCatalogListBase: () => ['website-sync-catalog-list'] as const,
  websiteSyncLogDetail: (id: string) => ['website-sync-log-detail', id] as const,
  websiteSyncQueue: (status: string, search: string, page: number, since?: string) =>
    ['website-sync-queue', status, search, page, since ?? 'all'] as const,
  websiteSyncQueueBase: () => ['website-sync-queue'] as const,
  websiteSyncActivity: (
    status: string,
    since: string,
    action: string,
    search: string,
    page: number
  ) => ['website-sync-activity', status, since, action, search, page] as const,
  websiteSyncActivityBase: () => ['website-sync-activity'] as const,

  courseAttempts: (course: string, includeInactive = false) =>
    ['courseAttempts', course, includeInactive] as const,
  courseAttemptsAll: () => ['courseAttempts', 'all'] as const,
  courseAttemptsNew: (since: string) => ['courseAttempts', 'new', since] as const,

  chatbotFaqs: (search: string) => ['chatbot-faqs', search] as const,
  chatbotTickets: () => ['chatbot-tickets'] as const,
  chatbotMessages: (ticketId: string) => ['chatbot-messages', ticketId] as const,
  chatbotMissedQueries: (since: string) => ['chatbot-missed-queries', since] as const,
  chatbotPersistentUsers: (since: string) => ['chatbot-persistent-users', since] as const,
  chatbotQueryStats: (since: string) => ['chatbot-query-stats', since] as const,
} as const
